import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  Link2, Copy, Check, Mail, ExternalLink, Clock, 
  Loader2, AlertTriangle, Send
} from 'lucide-react';

/**
 * SendToTaskGenerator - Generate unique tracking links for external responses
 * 
 * Props:
 * - projectId: string
 * - moduleType: 'RFI' | 'SI' | 'Notice' | 'Claim' | 'Drawing' | 'DelayEvent'
 * - moduleEntityId: string
 * - moduleReference: string (e.g., "RFI-001", "SI-045")
 * - recipientEmail?: string (optional, for pre-filling email composition)
 * - onLinkGenerated?: callback when link is created
 */
const SendToTaskGenerator = ({ 
  projectId, 
  moduleType, 
  moduleEntityId, 
  moduleReference,
  recipientEmail,
  onLinkGenerated 
}) => {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const baseUrl = typeof window !== 'undefined' 
    ? `${window.location.origin}` 
    : process.env.REACT_APP_BACKEND_URL;

  const generateLink = async () => {
    if (!moduleEntityId) {
      toast.error('Cannot generate link: Module ID is missing');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await api.post('/correspondence/send-to-task/generate', null, {
        params: {
          project_id: projectId,
          module_type: moduleType,
          module_entity_id: moduleEntityId,
          module_reference: moduleReference || `${moduleType}-${moduleEntityId.slice(0, 8)}`
        }
      });
      
      setGeneratedToken(response.data);
      toast.success('Response link generated successfully');
      
      if (onLinkGenerated) {
        onLinkGenerated(response.data);
      }
    } catch (error) {
      console.error('Send-to-task error:', error);
      const errorMessage = error.response?.data?.detail;
      let displayError = 'Unknown error';
      if (typeof errorMessage === 'string') {
        displayError = errorMessage;
      } else if (errorMessage && typeof errorMessage === 'object') {
        displayError = errorMessage.msg || errorMessage.message || JSON.stringify(errorMessage);
      } else if (error.message) {
        displayError = error.message;
      }
      toast.error('Failed to generate link: ' + displayError);
    } finally {
      setGenerating(false);
    }
  };

  const getResponseUrl = () => {
    if (!generatedToken) return '';
    return `${baseUrl}/respond/${generatedToken.token}`;
  };

  const getEmailTemplate = () => {
    const responseUrl = getResponseUrl();
    const ref = moduleReference || `${moduleType}-${moduleEntityId?.slice(0, 8) || 'N/A'}`;
    const subject = encodeURIComponent(`Response Request: ${ref}`);
    const body = encodeURIComponent(`Dear Recipient,

We require your response regarding ${ref}.

Please click the secure link below to submit your response:
${responseUrl}

This link will expire on ${generatedToken ? new Date(generatedToken.expires_at).toLocaleDateString() : 'N/A'}.

Thank you for your prompt attention to this matter.

Best regards`);
    
    return `mailto:${recipientEmail || ''}?subject=${subject}&body=${body}`;
  };

  const handleCopyLink = async () => {
    const linkUrl = getResponseUrl();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(linkUrl);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = linkUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      // Show the link in a prompt as last resort
      window.prompt('Copy this link:', linkUrl);
    }
  };

  const handleCopyEmailTemplate = async () => {
    const ref = moduleReference || `${moduleType}-${moduleEntityId?.slice(0, 8) || 'N/A'}`;
    const emailContent = `Response Request: ${ref}

Please respond to ${ref} using this secure link:
${getResponseUrl()}

This link expires: ${generatedToken ? new Date(generatedToken.expires_at).toLocaleDateString() : 'N/A'}`;
    
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(emailContent);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = emailContent;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setEmailCopied(true);
      toast.success('Email template copied');
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      window.prompt('Copy this template:', emailContent);
    }
  };

  const handleReset = () => {
    setGeneratedToken(null);
    setCopied(false);
    setEmailCopied(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) handleReset(); }}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="rounded-sm text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
          data-testid="generate-send-to-task-btn"
        >
          <Link2 className="h-3.5 w-3.5 sm:mr-1.5" />
          <span className="hidden sm:inline">Send-to-Task</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-barlow font-bold uppercase flex items-center gap-2">
            <Send className="h-5 w-5 text-blue-600" />
            Generate Response Link
          </DialogTitle>
          <DialogDescription>
            Create a unique tracking link that external parties can use to submit responses.
          </DialogDescription>
        </DialogHeader>

        {!generatedToken ? (
          <div className="space-y-4">
            {/* Context Info */}
            <div className="bg-slate-50 rounded-sm p-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Link Details
              </h4>
              <div className="text-sm space-y-1">
                <p><span className="text-slate-500">Module:</span> {moduleType}</p>
                <p><span className="text-slate-500">Reference:</span> {moduleReference || `${moduleType}-${moduleEntityId?.slice(0, 8) || 'N/A'}`}</p>
                <p><span className="text-slate-500">Validity:</span> 30 days</p>
              </div>
            </div>

            {/* Info Alert */}
            <Alert className="bg-blue-50 border-blue-200">
              <AlertTriangle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-xs text-blue-700">
                The generated link allows external parties to submit responses that will be 
                automatically captured as correspondence for this {moduleType.toLowerCase()}.
              </AlertDescription>
            </Alert>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setOpen(false)} 
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button 
                onClick={generateLink} 
                disabled={generating}
                className="flex-1 rounded-sm bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-generate-link-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4 mr-2" />
                    Generate Link
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Success State */}
            <div className="bg-green-50 border border-green-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Check className="h-5 w-5 text-green-600" />
                <h4 className="font-semibold text-green-700">Link Generated!</h4>
              </div>
              <div className="flex items-center gap-2 text-xs text-green-600">
                <Clock className="h-3 w-3" />
                Expires: {new Date(generatedToken.expires_at).toLocaleString()}
              </div>
            </div>

            {/* Generated Link */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Response Link
              </Label>
              <div className="flex gap-2 mt-1">
                <Input 
                  value={getResponseUrl()} 
                  readOnly 
                  className="font-mono text-xs rounded-sm"
                  data-testid="generated-link-input"
                />
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={handleCopyLink}
                  className="rounded-sm flex-shrink-0"
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Usage Tips */}
            <div className="bg-slate-50 rounded-sm p-3 text-xs text-slate-500">
              <p className="font-medium text-slate-600 mb-1">Usage Tips:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Include this link in your outgoing correspondence</li>
                <li>Responses will be automatically captured as correspondence</li>
                <li>Each link can only be used once</li>
                <li>Link expires after 30 days</li>
              </ul>
            </div>

            {/* Close Button */}
            <Button 
              onClick={() => setOpen(false)} 
              className="w-full rounded-sm"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendToTaskGenerator;
